import { useState } from "react";
import { toast } from "sonner";
import { Bot, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { revokeMcpConnection, type McpConnection } from "@/services/mcp-connection.service";
import { CLIENT_LABELS } from "./mcp-setup";

function statusBadge(c: McpConnection): { label: string; cls: string } {
	if (c.revoked_at) return { label: "Revoked", cls: "bg-danger-subtle text-danger border-danger/20" };
	if (c.expires_at && new Date(c.expires_at).getTime() <= Date.now()) {
		return { label: "Expired", cls: "bg-muted-subtle text-muted-foreground border-border" };
	}
	return { label: "Active", cls: "bg-success-subtle text-success border-success/20" };
}

export function McpConnectionsList({
	connections,
	projectNames,
	onEdit,
	onRevoked,
}: {
	connections: McpConnection[];
	projectNames: Map<string, string>;
	onEdit: (c: McpConnection) => void;
	onRevoked: (id: string) => void;
}) {
	const [revoking, setRevoking] = useState<string | null>(null);

	async function handleRevoke(c: McpConnection) {
		if (!window.confirm(`Revoke "${c.name}"? The agent using it loses access immediately.`)) return;
		setRevoking(c.id);
		try {
			await revokeMcpConnection(c.id);
			toast.success("Connection revoked.");
			onRevoked(c.id);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to revoke.");
		} finally {
			setRevoking(null);
		}
	}

	if (!connections.length) {
		return (
			<div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-8 text-center">
				<Bot className="h-6 w-6 text-muted-foreground" />
				<p className="text-sm font-medium text-foreground">No agents connected</p>
				<p className="max-w-sm text-xs text-muted-foreground">
					Create a connection to let Claude, Codex, Cursor or another MCP client manage your
					tasks and tickets.
				</p>
			</div>
		);
	}

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full text-left text-xs">
				<thead className="border-b border-border bg-muted-subtle text-muted-foreground">
					<tr>
						{["Name", "Agent", "Token", "Permissions", "Projects", "Last used", "Status", ""].map((h) => (
							<th key={h} className="whitespace-nowrap px-3 py-2 font-medium">
								{h}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{connections.map((c) => {
						const badge = statusBadge(c);
						const projects = c.project_ids.length
							? c.project_ids.map((id) => projectNames.get(id) ?? "Unknown").join(", ")
							: "All my projects";
						return (
							<tr key={c.id} className="border-b border-border last:border-b-0">
								<td className="whitespace-nowrap px-3 py-2 font-medium text-foreground">{c.name}</td>
								<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
									{CLIENT_LABELS[c.client] ?? c.client}
								</td>
								<td className="whitespace-nowrap px-3 py-2 font-mono text-[11px]">{c.token_prefix}…</td>
								<td className="px-3 py-2">
									<div className="flex min-w-48 flex-wrap gap-1">
										{c.permissions.map((p) => (
											<span
												key={p}
												className="rounded-md bg-muted-subtle px-1.5 py-0.5 text-[10px] text-muted-foreground"
											>
												{p}
											</span>
										))}
									</div>
								</td>
								<td className="max-w-48 truncate px-3 py-2 text-muted-foreground" title={projects}>
									{projects}
								</td>
								<td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
									{c.last_used_at ? new Date(c.last_used_at).toLocaleString() : "Never"}
								</td>
								<td className="whitespace-nowrap px-3 py-2">
									<span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>
										{badge.label}
									</span>
								</td>
								<td className="whitespace-nowrap px-3 py-2">
									{!c.revoked_at && (
										<div className="flex justify-end gap-2">
											<Button type="button" size="xs" variant="outline" onClick={() => onEdit(c)}>
												<Pencil className="h-3 w-3" />
												Edit
											</Button>
											<Button
												type="button"
												size="xs"
												variant="destructive_outline"
												disabled={revoking === c.id}
												onClick={() => handleRevoke(c)}
											>
												<Trash2 className="h-3 w-3" />
												{revoking === c.id ? "Revoking…" : "Revoke"}
											</Button>
										</div>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
